import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Container,
} from 'react-bootstrap';
import { useGetDailyStocksQuery, useUpdateDailyStockMutation } from '../../slices/dailyStocksApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';

const DailyStockNoteSearchReplaceScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [replacementText, setReplacementText] = useState('');
  const [previewResults, setPreviewResults] = useState([]);
  const [isReplacing, setIsReplacing] = useState(false);

  const { data: dailyStocksData, isLoading, error } = useGetDailyStocksQuery();
  const [updateDailyStock] = useUpdateDailyStockMutation();

  // Preview matches in notes
  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const matches = dailyStocksData?.dailyStocks
      ?.filter((dailyStock) =>
        dailyStock.note?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map((dailyStock) => ({
        dailyStockId: dailyStock._id,
        date: dailyStock.date,
        originalNote: dailyStock.note,
      }));

    setPreviewResults(matches || []);
  };

  // Replace notes
  const handleReplace = async () => {
    if (!replacementText.trim()) return toast.error('Replacement text required');
    if (!searchTerm.trim()) return toast.error('Search term required');

    const affectedDailyStocks = dailyStocksData?.dailyStocks?.filter((dailyStock) =>
      dailyStock.note?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!affectedDailyStocks || affectedDailyStocks.length === 0) {
      return toast.info('No matching notes found');
    }

    setIsReplacing(true);

    try {
      for (const dailyStock of affectedDailyStocks) {
        const updatedNote = dailyStock.note.replace(
          new RegExp(searchTerm, 'gi'),
          replacementText
        );

        await updateDailyStock({
          dailyStockId: dailyStock._id,
          dailyStockItems: dailyStock.dailyStockItems,
          customerDetails: dailyStock.customerDetails,
          note: updatedNote,
          status: dailyStock.status,
          date: dailyStock.date,
        }).unwrap();
      }

      toast.success(`Replaced "${searchTerm}" with "${replacementText}" in notes`);
      setPreviewResults([]);
      setSearchTerm('');
      setReplacementText('');
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed');
    } finally {
      setIsReplacing(false);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <h1>Search & Replace in DailyStock Notes</h1>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Search for text in notes"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Replacement text"
            value={replacementText}
            onChange={(e) => setReplacementText(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Button
            variant="primary"
            onClick={handleSearch}
            className="me-2"
            disabled={!searchTerm.trim() || isReplacing}
          >
            Preview Matches
          </Button>
          <Button
            variant="success"
            onClick={handleReplace}
            disabled={!replacementText.trim() || isReplacing}
          >
            Replace All
          </Button>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error loading dailyStocks'}
        </Message>
      ) : previewResults.length === 0 ? (
        <Alert variant="info">
          No matching notes found. Try a different search term.
        </Alert>
      ) : (
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              <th>DATE</th>
              <th>ORIGINAL NOTE</th>
            </tr>
          </thead>
          <tbody>
            {previewResults.map((item) => (
              <tr key={item.dailyStockId}>
                <td>{new Date(item.date).toLocaleDateString()}</td>
                <td>{item.originalNote}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default DailyStockNoteSearchReplaceScreen;
